import React, { Fragment, useState, useEffect } from "react";
import { getAwsAirflowData, getAwsAirflowDataFromDb } from "../../../services/awsService";
import AirflowClusterPoolOne from "./AirflowClusterPoolOne";
import AirflowClusterPoolTwo from "./AirflowClusterPoolTwo";
import {
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
} from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import moment from "moment";

const AwsAirflowDashboard = () => {
  console.log("AwsAirflowDashboard");
  // Data states
  const [airflowData, setAirflowData] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(
    moment().format("YYYY-MM-DD HH:mm:ss")
  );
  const [loadingElement, setLoadingElement] = useState(true);
  const [env, setEnv] = useState("prod");

  // Function to refresh all data
  const handleRescan = async (environment = env, refreshFlag = true) => {
    console.log("handleRescan called with:", environment, refreshFlag, "current loading state:", loadingElement);
    
    setLoadingElement(true);
    
    // Safety timeout to ensure loading never gets stuck
    const loadingTimeout = setTimeout(() => {
      console.warn("Loading timeout reached (15s), forcing loading state to false");
      setLoadingElement(false);
    }, 15000); // 15 second timeout
    
    try {
      console.log("🔄 Fetching Airflow data for environment:", environment);
      console.log("🔄 Refresh flag:", refreshFlag, refreshFlag ? "(FORCING FRESH DATA)" : "(USING CACHE)");
      
      if (!refreshFlag) {
        console.log("📦 Using CACHED data - this will be faster but may not be the most recent");
      } else {
        console.log("🆕 Using FRESH data - this will take longer but ensures latest information");
      }
      
      const startTime = Date.now();
      const data = await getAwsAirflowData(environment, refreshFlag);
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      console.log(`⏱️  API call took ${duration}ms (${(duration/1000).toFixed(2)}s)`);
      
      // Log data source confirmation
      if (!refreshFlag) {
        console.log("✅ CACHED data successfully retrieved");
      } else {
        console.log("✅ FRESH data successfully retrieved");
      }
      
      console.log("📊 Raw Airflow data received:", data);
      console.log("📊 Data type:", typeof data);
      console.log("📊 Data is array:", Array.isArray(data));
      console.log("📊 Data length:", data.length);
      
      // Log sample data structure for debugging
      if (data.length > 0) {
        console.log("Sample DAG structure:", data[0]);
        console.log("Sample DAG keys:", Object.keys(data[0] || {}));
      }
      
      // Handle different possible data structures more robustly
      let dags = [];
      if (data) {
        if (Array.isArray(data)) {
          console.log("Data is already an array");
          dags = data;
        } else if (data.dags && Array.isArray(data.dags)) {
          console.log("Found dags array in data.dags");
          dags = data.dags;
        } else if (data.data && Array.isArray(data.data)) {
          console.log("Found array in data.data");
          dags = data.data;
        } else if (data.results && Array.isArray(data.results)) {
          console.log("Found array in data.results");
          dags = data.results;
        } else {
          console.log("Could not find DAGs array in data structure, checking all properties");
          // Try to find any array property
          const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
          if (arrayKeys.length > 0) {
            console.log("Found array properties:", arrayKeys);
            dags = data[arrayKeys[0]]; // Use the first array found
          } else {
            console.log("No array properties found");
            dags = [];
          }
        }
      } else {
        console.log("No data received from API");
        dags = [];
      }
      
      console.log("Final dags to process (rescan):", dags);
      console.log("Number of DAGs:", dags ? dags.length : 0);
      
      // Additional validation and data cleaning
      if (dags.length === 0) {
        console.warn("⚠️  No DAGs found in response - this might indicate:");
        console.warn("   - Cache is empty (try refresh=true)");
        console.warn("   - Environment has no DAGs");
        console.warn("   - API endpoint issue");
        console.warn("   - Data structure parsing issue");
      } else {
        // Clean and validate DAG data to prevent runtime errors
        const cleanedDags = dags.map(dag => ({
          ...dag,
          dag_id: dag.dag_id || 'unknown',
          runs: Array.isArray(dag.runs) ? dag.runs.map(run => ({
            ...run,
            state: run.state || 'unknown',
            run_id: run.run_id || 'unknown',
            start_date: run.start_date || new Date().toISOString(),
            duration: run.duration || '-'
          })) : []
        }));
        
        console.log("Cleaned DAG data:", cleanedDags.slice(0, 2)); // Log first 2 DAGs
        dags = cleanedDags;
      }
      
      setAirflowData(dags);
      setLastRefreshTime(moment().format("YYYY-MM-DD HH:mm:ss"));
      console.log("✅ Data set successfully, airflowData now has", dags.length, "items");
    } catch (error) {
      console.error("Error during rescan:", error);
      console.error("Error details:", error.message);
      setAirflowData([]);
    } finally {
      clearTimeout(loadingTimeout);
      setLoadingElement(false);
      console.log("Loading completed, setting loadingElement to false");
    }
  };

  // Load initial data
  useEffect(() => {
    handleRescan("prod", false); // Use cached data for initial load
  }, []);

  // Authentication info
  const { accounts } = useMsal();

  return (
    <Fragment>
      <div className="row pt-5">
        {/* <h1 className="title text-center">Maw</h1> */}
      </div>
      <div className="row justify-content-center">
        <div className="col-md-6">
          <ButtonGroup className="env-toggle">
            <Button
              variant={env === "prod" ? "primary" : "outline-primary"}
              onClick={() => {
                setEnv("prod");
                handleRescan("prod", true);
              }}
              className={env === "prod" ? "active" : ""}
            >
              PROD
            </Button>
            <Button
              variant={env === "dev" ? "primary" : "outline-primary"}
              onClick={() => {
                setEnv("dev");
                handleRescan("dev", true);
              }}
              className={env === "dev" ? "active" : ""}
            >
              DEV
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <style jsx>{`
        .env-toggle {
          display: flex;
          justify-content: center;
          margin: 20px 0;
        }
        .env-toggle .btn {
          min-width: 120px;
          transition: all 0.3s ease;
          border-radius: 0;
        }
        .env-toggle .btn:first-child {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }
        .env-toggle .btn:last-child {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
        .env-toggle .btn.active {
          font-weight: bold;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      {/* Main content area with cluster pools */}
      <Col className="mt-5" lg={12}>
        <Card>
          <Card.Header>
            <Card.Title>MWAA (Clusters)</Card.Title>
            {lastRefreshTime && (
              <div
                style={{
                  textAlign: "center",
                  fontSize: "0.9rem",
                  color: "#3d4465",
                }}
              >
                Last refreshed: {lastRefreshTime}
              </div>
            )}
          </Card.Header>
          <Card.Body>
            <AirflowClusterPoolOne dynamicData={airflowData} />
            <AirflowClusterPoolTwo dynamicData={airflowData} />
          </Card.Body>
          <div className="row mb-3">
            <div className="col text-center">
              <Button 
                variant="primary" 
                onClick={() => handleRescan(env, true)}
                disabled={loadingElement}
                className="me-2"
              >
                {loadingElement ? "Loading..." : "Fresh Data"}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => handleRescan(env, false)}
                disabled={loadingElement}
                className="me-2"
              >
                {loadingElement ? "Loading..." : "Cached Data"}
              </Button>
              {loadingElement && (
                <Button 
                  variant="warning" 
                  onClick={() => {
                    console.log("Emergency stop - clearing loading state");
                    setLoadingElement(false);
                  }}
                  size="sm"
                >
                  Stop Loading
                </Button>
              )}
            </div>
          </div>
        </Card>
      </Col>

      {/* Modern Loading Overlay */}
      {loadingElement && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1050,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "2rem",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
              minWidth: "200px",
            }}
          >
            {/* Pulsing dots loader */}
            <div style={{ display: "flex", gap: "8px" }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: "#007bff",
                    animation: `pulse 1.4s ease-in-out ${i * 0.16}s infinite both`,
                  }}
                />
              ))}
            </div>
            <div style={{ 
              color: "#333", 
              fontSize: "1rem", 
              fontWeight: "500",
              textAlign: "center"
            }}>
              Loading Airflow Data...
            </div>
            <div style={{ 
              color: "#666", 
              fontSize: "0.875rem",
              textAlign: "center"
            }}>
              Environment: {env.toUpperCase()}
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes pulse {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default AwsAirflowDashboard;

